import type { ReactNode } from 'react';

import type { SnippetLang } from '../../utils';

import { AndroidInstallation } from './installation/AndroidInstallation';
import { GoInstallation } from './installation/GoInstallation';
import { ReactNativeInstallation } from './installation/ReactNativeInstallation';
import type { InstallableSdk } from './types';

export interface SnippetConfig {
  event: string;
  withCommandLine?: boolean;
  snippets: Array<{ title: string; packageManager: string; language: SnippetLang }>;
  supplemental?: {
    before?: (selectedPackageManager: string, onPackageManagerChange: (pm: string) => void) => ReactNode;
    after?: (selectedPackageManager: string, onPackageManagerChange: (pm: string) => void) => ReactNode;
  };
}

export const installSnippetConfig: Record<InstallableSdk, SnippetConfig> = {
  'android-client-sdk': {
    event: 'android-client-installation',
    snippets: [
      { title: 'Gradle Groovy', packageManager: 'groovy', language: 'gradle' },
      { title: 'Gradle Kotlin', packageManager: 'kotlin', language: 'kotlin' },
    ],
    supplemental: { after: AndroidInstallation },
  },
  'dotnet-server-sdk': {
    event: 'dotnet-server-installation',
    withCommandLine: true,
    snippets: [{ title: 'Shell', packageManager: 'csharp', language: 'powershell' }],
  },
  'go-server-sdk': {
    event: 'go-server-installation',
    withCommandLine: true,
    snippets: [{ title: 'Shell', packageManager: 'go', language: 'bash' }],
    supplemental: { before: GoInstallation },
  },
  'ios-client-sdk': {
    event: 'ios-client-installation',
    snippets: [
      { title: 'Swift', packageManager: 'packageSwift', language: 'swift' },
      { title: 'Podfile', packageManager: 'podfile', language: 'ruby' },
      { title: 'Cartfile', packageManager: 'cartfile', language: 'ruby' },
    ],
  },
  'java-server-sdk': {
    event: 'java-server-installation',
    snippets: [
      { title: 'XML', packageManager: 'xml', language: 'markup' },
      { title: 'Gradle', packageManager: 'gradle', language: 'gradle' },
    ],
  },
  'js-client-sdk': {
    event: 'js-client-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
  },
  'node-client-sdk': {
    event: 'node-client-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
  },
  'node-server-sdk': {
    event: 'node-server-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
  },
  'python-server-sdk': {
    event: 'python-server-installation',
    withCommandLine: true,
    snippets: [{ title: 'Shell', packageManager: 'pip', language: 'bash' }],
  },
  'react-client-sdk': {
    event: 'react-client-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
  },
  'react-native-client-sdk': {
    event: 'react-native-client-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
    supplemental: { after: ReactNativeInstallation },
  },
  'vue-client-sdk': {
    event: 'vue-client-installation',
    withCommandLine: true,
    snippets: [
      { title: 'yarn', packageManager: 'yarn', language: 'bash' },
      { title: 'npm', packageManager: 'npm', language: 'bash' },
      { title: 'pnpm', packageManager: 'pnpm', language: 'bash' },
    ],
  },
};
